import * as React from "react";
import * as Reach from "@reach/router";
import { useAsyncTask, AsyncTask } from "@/mixins/react-hooks-async";
import OverlayContext, { initOverlayStore } from "@/contexts/Overlay";
import SnackbarContext, { initSnackBarStore } from "@/contexts/SnackBar";
import FormContext, { initFormStore, updateFormValues } from "@/contexts/Form";
import form, { FormValues } from "@/utils/form";

export const useAsyncTaskLoading = <T, Args extends unknown[]>(
  func: (c: AbortController, ...args: Args) => Promise<T>
): AsyncTask<T, Args> => {
  const overlayContext = React.useContext(OverlayContext) || initOverlayStore(false);
  const task = useAsyncTask<T, Args>(func);

  React.useEffect(() => {
    if (task.started && task.pending) {
      overlayContext.open();
    } else {
      overlayContext.close();
    }
    return () => overlayContext.close();
  }, [task.started, task.pending]);

  return task;
};

export const useQueryParams = () => {
  return new URLSearchParams(Reach.useLocation().search);
};

export const useSnackBarCard: (mode?: "default" | "info" | "alert" | "warn") => (message: string) => void = (
  mode = "default"
) => {
  const { card, open, close } =
    React.useContext(SnackbarContext) || initSnackBarStore({ open: false, message: "", mode: "default" });

  const handleOpen = React.useCallback((message: string) => {
    open({ message, mode });
  }, []);

  React.useEffect(() => {
    if (card.open) {
      setTimeout(() => {
        close();
      }, 3000);
    }
  }, [card.open]);

  return handleOpen;
};

export const useFormHandler: (key: keyof FormValues, validation?: (value: any) => string) => (value: any) => void = (
  key,
  validation
) => {
  const { dispatch } = React.useContext(FormContext) || initFormStore(form);
  const handleChangeValue = React.useCallback((value: string) => {
    let message = "";
    if (validation) {
      message = validation(value);
    }
    dispatch(
      updateFormValues({
        values: {
          [key]: value,
        },
        validates: {
          [key]: message,
        },
      })
    );
  }, []);

  return handleChangeValue;
};

export const useFormHandlerToggleRequired: (
  key: keyof FormValues,
  validation: (value: any, required: boolean) => string
) => (value: any, required: boolean) => void = (key, validation) => {
  const { dispatch } = React.useContext(FormContext) || initFormStore(form);
  const handleChangeValue = React.useCallback((value: string, required: boolean) => {
    let message = "";
    message = validation(value, required);
    dispatch(
      updateFormValues({
        values: {
          [key]: value,
        },
        validates: {
          [key]: message,
        },
      })
    );
  }, []);

  return handleChangeValue;
};

export const usePrevious = <T>(value: T) => {
  const ref = React.useRef<T>();

  React.useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};
